import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import { ToastProvider } from './contexts/ToastContext'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import CookieBanner from './components/layout/CookieBanner'
import RGPDButton from './components/layout/RGPDButton'
import RGPDPanel from './components/layout/RGPDPanel'
import PrivateRoute from './components/auth/PrivateRoute'
import AdminRoute from './components/auth/AdminRoute'
import LandingPage from './pages/LandingPage'
import Login from './pages/Login'
import Register from './pages/Register'
import Dashboard from './pages/Dashboard'
import Courses from './pages/Courses'
import CoursePage from './pages/CoursePage'
import LessonPage from './pages/LessonPage'
import Campaigns from './pages/Campaigns'
import Community from './pages/Community'
import Profile from './pages/Profile'
import Legal from './pages/Legal'
import Privacy from './pages/Privacy'
import Cookies from './pages/Cookies'
import Transparency from './pages/Transparency'
import Terms from './pages/Terms'
import Contact from './pages/Contact'
import About from './pages/About'
import FAQ from './pages/FAQ'
import Impact from './pages/Impact'
// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard'
import UsersManagement from './pages/admin/UsersManagement'
import CoursesManagement from './pages/admin/CoursesManagement'
import CourseContentEditor from './pages/admin/CourseContentEditor'
import CampaignsManagement from './pages/admin/CampaignsManagement'
import Moderation from './pages/admin/Moderation'
import AuditLogs from './pages/admin/AuditLogs'
import ContactManagement from './pages/admin/ContactManagement'
import NewsletterManagement from './pages/admin/NewsletterManagement'

function App() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [rgpdPanelOpen, setRgpdPanelOpen] = useState(false)

  useEffect(() => {
    if (import.meta.env.DEV) console.log('App mounted, checking session...')
    // Verificar sesión inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (import.meta.env.DEV) console.log('Session loaded, user:', session?.user?.email || 'No user')
    })

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (import.meta.env.DEV) console.log('Auth state changed:', _event, session?.user?.email || 'No user')
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleRgpdOpen = () => {
    if (import.meta.env.DEV) console.log('🔵 Set rgpdPanelOpen to TRUE')
    setRgpdPanelOpen(true)
    if (import.meta.env.DEV) console.log('🟢 rgpdPanelOpen state changed to:', rgpdPanelOpen)
  }

  const handleRgpdClose = () => {
    if (import.meta.env.DEV) console.log('🔴 Closing RGPD Panel')
    setRgpdPanelOpen(false)
  }

  useEffect(() => {
    console.log('🟢 rgpdPanelOpen state changed to:', rgpdPanelOpen)
  }, [rgpdPanelOpen])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-education/10 to-action/10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-education mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Cargando Learn to Lead Change...</p>
        </div>
      </div>
    )
  }

  return (
    <ToastProvider>
      <BrowserRouter>
        <div className="flex flex-col min-h-screen bg-white">
          <Header />
          <main className="flex-grow">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/registro" element={<Register />} />
            
            {/* Rutas protegidas */}
            <Route path="/dashboard" element={
              <PrivateRoute user={user}>
                <Dashboard user={user} />
              </PrivateRoute>
            } />
            <Route path="/cursos" element={
              <PrivateRoute user={user}>
                <Courses user={user} />
              </PrivateRoute>
            } />
            <Route path="/cursos/:courseId" element={
              <PrivateRoute user={user}>
                <CoursePage user={user} />
              </PrivateRoute>
            } />
            <Route path="/cursos/:courseId/leccion/:lessonId" element={
              <PrivateRoute user={user}>
                <LessonPage user={user} />
              </PrivateRoute>
            } />
            <Route path="/campanas" element={
              <PrivateRoute user={user}>
                <Campaigns user={user} />
              </PrivateRoute>
            } />
            <Route path="/comunidad" element={
              <PrivateRoute user={user}>
                <Community user={user} />
              </PrivateRoute>
            } />
            <Route path="/perfil" element={
              <PrivateRoute user={user}>
                <Profile user={user} />
              </PrivateRoute>
            } />

            {/* Rutas administrativas */}
            <Route path="/admin" element={
              <AdminRoute requiredRoles={['superadmin', 'admin', 'moderator', 'editor']}>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/usuarios" element={
              <AdminRoute requiredPermissions={['manage_users']}>
                <UsersManagement />
              </AdminRoute>
            } />
            <Route path="/admin/cursos" element={
              <AdminRoute requiredPermissions={['manage_courses', 'create_course']}>
                <CoursesManagement />
              </AdminRoute>
            } />
            <Route path="/admin/cursos/:courseId/contenido" element={
              <AdminRoute requiredPermissions={['manage_courses', 'create_course']}>
                <CourseContentEditor />
              </AdminRoute>
            } />
            <Route path="/admin/campanas" element={
              <AdminRoute requiredPermissions={['manage_campaigns']}>
                <CampaignsManagement />
              </AdminRoute>
            } />
            <Route path="/admin/moderacion" element={
              <AdminRoute requiredPermissions={['moderate_forum']}>
                <Moderation />
              </AdminRoute>
            } />
            <Route path="/admin/logs" element={
              <AdminRoute requiredPermissions={['access_logs']}>
                <AuditLogs />
              </AdminRoute>
            } />
            <Route path="/admin/contacto" element={
              <AdminRoute requiredPermissions={['manage_users']}>
                <ContactManagement />
              </AdminRoute>
            } />
            <Route path="/admin/newsletter" element={
              <AdminRoute requiredPermissions={['manage_users']}>
                <NewsletterManagement />
              </AdminRoute>
            } />

            {/* Páginas públicas */}
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/impact" element={<Impact />} />
            
            {/* Páginas legales - públicas */}
            <Route path="/legal" element={<Legal />} />
            <Route path="/privacidad" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/transparencia" element={<Transparency />} />
            <Route path="/terminos" element={<Terms />} />
          </Routes>
        </main>
        <Footer />
        
        {/* Cookie Banner */}
        <CookieBanner />
        
        {/* RGPD Button y Panel */}
        <RGPDButton onClick={handleRgpdOpen} />
        <RGPDPanel 
          isOpen={rgpdPanelOpen} 
          onClose={handleRgpdClose}
          user={user}
        />
      </div>
    </BrowserRouter>
  </ToastProvider>
  )
}

export default App
